import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import DonutChart from '@/components/ui/DonutChart';
import MetricCard from '@/components/ui/MetricCard';
import { useInstructorHighlights, InstructorHighlight } from '@/features/profile/hooks/useInstructorHighlights';
import { Plus } from 'lucide-react';

const InstructorJourneyHighlightsSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { highlights: highlightsData, isLoading, error, refetch } = useInstructorHighlights();

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  // Calculate total number of pages based on visible cards
  const cardsPerView = 3; // Show 3 cards per view
  const totalCards = highlightsData ? 9 : 0; // 9 cards total
  const totalPages = Math.ceil(totalCards / cardsPerView);

  // Handle scroll to update current index
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 135; // w-[135px] = 135px
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.min(newIndex, totalPages - 1));
    }
  };

  // Scroll to specific page
  const scrollToPage = (pageIndex: number) => {
    if (scrollContainerRef.current) {
      const scrollLeft = pageIndex * cardWidth * cardsPerView;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const cardWidth = 135;

  const handleAddClasses = () => {
    navigate('/calendar');
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-gray-400">Loading highlights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-red-400">Error loading highlights</div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-white font-roboto mb-4">
        {t('home.journey_highlights.title')}
      </h2>
      
      <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
        <div 
          className="w-full overflow-x-auto pb-2 scrollbar-hide" 
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div className="flex space-x-4">
            {/* Card 1: Total Classes Taught with Donut Chart */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px]">
              {/* Title */}
              <div className="mb-2">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.total_classes_taught')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.instructor_highlights.this_month')}</p>
              </div>
              
              {/* Custom Donut Chart with numbers inside chart */}
              <div className="flex flex-col items-center">
                {/* Donut Chart */}
                <div className="relative" style={{ width: 64, height: 64 }}>
                  <svg 
                    width={64} 
                    height={64} 
                    className="transform -rotate-90"
                    viewBox="0 0 64 64"
                  >
                    {/* Background circle */}
                    <circle
                      cx={32}
                      cy={32}
                      r={28}
                      fill="none"
                      stroke="#524545"
                      strokeWidth={8}
                    />
                    
                    {/* Segments */}
                    {(() => {
                      const segments = [
                        {
                          label: t('home.highlights.group'),
                          value: highlightsData?.totalClasses.group || 0,
                          color: '#9852DB',
                          percentage: highlightsData?.totalClasses.total > 0 
                            ? (highlightsData.totalClasses.group / highlightsData.totalClasses.total) * 100 
                            : 0
                        },
                        {
                          label: t('home.highlights.private'),
                          value: highlightsData?.totalClasses.private || 0,
                          color: '#416FF0',
                          percentage: highlightsData?.totalClasses.total > 0 
                            ? (highlightsData.totalClasses.private / highlightsData.totalClasses.total) * 100 
                            : 0
                        }
                      ];
                      
                      const circumference = 2 * Math.PI * 28;
                      let currentOffset = 0;
                      
                      return segments.map((segment, index) => {
                        const segmentLength = (segment.percentage / 100) * circumference;
                        const segmentOffset = currentOffset;
                        
                        currentOffset += segmentLength;
                        
                        return (
                          <circle
                            key={index}
                            cx={32}
                            cy={32}
                            r={28}
                            fill="none"
                            stroke={segment.color}
                            strokeWidth={8}
                            strokeDasharray={`${segmentLength} ${circumference}`}
                            strokeDashoffset={-segmentOffset}
                            strokeLinecap="round"
                          />
                        );
                      });
                    })()}
                  </svg>
                  
                </div>
                
                
                {/* Legend with category names and values */}
                <div className="flex justify-center space-x-4 mt-3">
                  {(() => {
                    const segments = [
                      {
                        label: t('home.highlights.group'),
                        value: highlightsData?.totalClasses.group || 0,
                        color: '#9852DB',
                        percentage: highlightsData?.totalClasses.total > 0 
                          ? (highlightsData.totalClasses.group / highlightsData.totalClasses.total) * 100 
                          : 0
                      },
                      {
                        label: t('home.highlights.private'),
                        value: highlightsData?.totalClasses.private || 0,
                        color: '#416FF0',
                        percentage: highlightsData?.totalClasses.total > 0 
                          ? (highlightsData.totalClasses.private / highlightsData.totalClasses.total) * 100 
                          : 0
                      }
                    ];
                    
                    return segments.map((segment, index) => (
                      <div key={index} className="flex items-center space-x-1">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: segment.color }}
                        />
                      <span className="text-[8px] text-white whitespace-nowrap">
                        {segment.label} ({segment.value})
                      </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Card 2: Revenue with Add Classes Button */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.revenue_this_month')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.instructor_highlights.this_month')}</p>
              </div>
              
              {/* Revenue Value with real data */}
              <div className="text-center mb-4 flex-1 flex items-center justify-center">
                <div className="space-y-1">
                  <p className="text-[28px] font-bold text-white leading-none">
                    <span className="text-[10px] font-light">
                      {highlightsData?.revenue.currency === 'BRL' ? 'R$' : '$'}
                    </span>
                    {highlightsData?.revenue.currency === 'BRL' 
                      ? ` ${highlightsData?.revenue.amount?.toLocaleString('pt-BR', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }) || '0,00'}`
                      : ` ${highlightsData?.revenue.amount?.toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }) || '0.00'}`
                    }
                  </p>
                </div>
              </div>
              
              {/* Add Classes Button */}
              <div className="flex justify-center">
                <button 
                  onClick={handleAddClasses}
                  className="bg-transparent border border-white rounded-[100px] flex items-center justify-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors w-full"
                >
                  <Plus className="w-3 h-3 text-white" />
                  <span className="text-[10px] font-bold text-white">{t('home.next_classes.add_class')}</span>
                </button>
              </div>
            </div>

            {/* Card 3: Most Active Student */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px]">
              {/* Title */}
              <div className="mb-2">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.students_taught')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.instructor_highlights.this_month')}</p>
              </div>
              
              {/* Student Avatar with FramedAvatar */}
              <div className="flex justify-center mb-3">
                {highlightsData?.mostActiveStudent.name && highlightsData.mostActiveStudent.name !== 'No students yet' ? (
                  <FramedAvatar
                    src={highlightsData.mostActiveStudent.avatarUrl}
                    alt={highlightsData.mostActiveStudent.name}
                    initials={highlightsData.mostActiveStudent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    size={36}
                    frameSize={{ w: 48, h: 52}}
                    beltRank={highlightsData.mostActiveStudent.beltRank || "white"}
                    fontSize={14}
                  />
                ) : (
                  <FramedAvatar
                    initials="?"
                    size={36}
                    frameSize={{ w: 48, h: 52}}
                    beltRank="white"
                    fontSize={14}
                  />
                )}
              </div>
              
              {/* Student Name with real data */}
              <div className="text-center mb-2">
                <p className="text-[12px] font-bold text-white font-roboto leading-[20px]">
                  {highlightsData?.mostActiveStudent.name || t('home.instructor_highlights.no_students_yet')}
                </p>
              </div>
              
              {/* Classes Count with real data */}
              <div className="text-center">
                <p className="text-[10px] text-neutral-300 font-normal leading-[16px] tracking-[-0.5px]">
                  <span className="font-bold text-white">
                    {highlightsData?.mostActiveStudent.classesCount || 0}
                  </span> {t('home.instructor_highlights.classes_together')}
                </p>
              </div>
            </div>

            {/* Card 4: Mat Time Utilization */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.mat_time_utilization')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
              </div>
              
              {/* Utilization Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.matUtilization.percentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    {t('home.instructor_highlights.utilization')}
                  </div>
                </div>
              </div>
              
              {/* Utilization Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.matUtilization.occupiedSlots || 0}</span>h {t('home.instructor_highlights.occupied')}
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.matUtilization.totalSlots || 0}</span>h {t('home.instructor_highlights.available')}
                </div>
              </div>
            </div>

            {/* Card 5: Booking Efficiency */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.booking_efficiency')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
              </div>
              
              {/* Efficiency Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.bookingEfficiency.percentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    {t('home.instructor_highlights.efficiency')}
                  </div>
                </div>
              </div>
              
              {/* Efficiency Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.bookingEfficiency.completedBookings || 0}</span> {t('home.instructor_highlights.completed')}
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="text-[10px] font-bold">{highlightsData?.bookingEfficiency.totalBookings || 0}</span> {t('home.instructor_highlights.total')}
                </div>
              </div>
            </div>

            {/* Card 6: Total Hours on the Mat */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.total_hours_on_mat')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
              </div>
              
              {/* Total Hours - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.totalHours.hours || 0}
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    {t('home.instructor_highlights.hours')}
                  </div>
                </div>
              </div>
              
              {/* Time Details - Bottom */}
              <div className="text-center">
                <div className="text-[10px] text-white font-normal opacity-70">
                  {t('home.instructor_highlights.teaching_time')}
                </div>
              </div>
            </div>

            {/* Card 7: Most Requested Technique */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.most_requested_technique')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
              </div>
              
              {/* Quantity - Large font */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.mostRequestedTechnique?.count || 0}
                  </div>
                </div>
              </div>
              
              {/* Technique Pill - Only show if technique exists */}
              {highlightsData?.mostRequestedTechnique?.name && (
                <div className="text-center">
                  <div className="flex justify-center">
                    <div className="flex px-2 py-1 bg-sossiety-red rounded-full max-w-full">
                      <span className="text-white text-[10px] font-medium truncate">
                        {highlightsData.mostRequestedTechnique.name}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card 8: Schedule Status */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">{t('home.instructor_highlights.your_schedule_status')}</p>
                <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
              </div>
              
              {/* Status Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.scheduleStatus.completedPercentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    {t('home.instructor_highlights.completed')}
                  </div>
                </div>
              </div>
              
              {/* Status Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.scheduleStatus.completed || 0}</span> {t('home.instructor_highlights.completed')}
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.scheduleStatus.missed || 0}</span> {t('home.instructor_highlights.missed')}
                </div>
              </div>
            </div>

            {/* Card 9: Achievement Milestone (show first reached milestone) */}
            {highlightsData?.achievements?.find(a => a.reached) && (() => {
              const achievement = highlightsData.achievements.find(a => a.reached);
              const getOrdinalNumber = (num: number) => {
                const suffixes = ['th', 'st', 'nd', 'rd'];
                const v = num % 100;
                return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
              };
              
              return (
                <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
                  {/* Title - Achievement Title */}
                  <div className="mb-3">
                    <p className="text-[12px] font-semibold text-white font-roboto mb-0">{achievement?.title || 'Milestone'}</p>
                    <p className="text-[10px] font-light text-white font-roboto">{t('home.journey_highlights.this_month')}</p>
                  </div>
                  
                  {/* Ordinal Number - Large font */}
                  <div className="text-center flex-1 flex items-center justify-center">
                    <div className="space-y-2">
                      <div className="text-[36px] font-bold text-white leading-none">
                        {achievement?.milestone ? getOrdinalNumber(achievement.milestone) : '1st'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Classes Completed - Bottom */}
                  <div className="text-center">
                    <div className="text-[10px] text-white font-normal opacity-70">
                      {achievement?.milestone === 1 
                        ? t('home.instructor_highlights.one_class_completed')
                        : t('home.instructor_highlights.classes_completed', { count: achievement?.milestone || 0 })
                      }
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Navigation dots */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-1.5 mt-4">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => scrollToPage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-gray-300 scale-125' 
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InstructorJourneyHighlightsSection;
